import { motion, useMotionValue, useSpring, useTransform } from "motion/react";
import type React from "react";
import { useCallback, useEffect, useRef } from "react";
import { twJoin } from "tailwind-merge";

import nobiLogo from "@/assets/images/nobi.png";
import { RuntimeMotionTransformImage } from "@/ui-adapters/MediaLayoutMotion";

import { NobiGlob } from "../NobiGlob/NobiGlob";

interface Props {
  size: "sm" | "lg";
  animateOnHover?: boolean;
}

export function AskNobiChatLogo({ size, animateOnHover }: Props): React.ReactNode {
  if (animateOnHover) {
    return <AskNobiChatLogoAnimated size={size} />;
  }

  return <AskNobiChatLogoStatic size={size} />;
}

export function AskNobiChatLogoStatic({ size }: Omit<Props, "animateOnHover">): React.ReactNode {
  return (
    <span className="shrink-0">
      <NobiGlob size={size} />
    </span>
  );
}

const ANIMATED_HOVER_INFLUENCE_RADIUS = 200;
const ANIMATED_MAX_TRANSLATE_RATIO = 0.2;

export function AskNobiChatLogoAnimated({ size }: Omit<Props, "animateOnHover">): React.ReactNode {
  const sizePx = size === "sm" ? 20 : 40;

  const containerRef = useRef<HTMLDivElement>(null);
  const translateX = useMotionValue(0);
  const translateY = useMotionValue(0);
  const rotate = useMotionValue(0);

  const springX = useSpring(translateX, { stiffness: 300, damping: 28 });
  const springY = useSpring(translateY, { stiffness: 300, damping: 28 });
  const springScale = useTransform([springX, springY], ([x, y]: number[]) => {
    return 1 + (2 * Math.max(Math.abs(x), Math.abs(y))) / sizePx;
  });
  const springRotate = useSpring(rotate, { stiffness: 180, damping: 14 });

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const el = containerRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;

      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      const elementRadius = sizePx / 2;

      if (dist >= ANIMATED_HOVER_INFLUENCE_RADIUS) {
        translateX.set(0);
        translateY.set(0);
        rotate.set(0);

        return;
      }

      if (dist < elementRadius) {
        translateX.set(0);
        translateY.set(0);
        rotate.set((dx / elementRadius) * 20);

        return;
      }

      rotate.set(0);

      // Remap dist from [elementRadius, INFLUENCE_RADIUS] → influence [1, 0]
      const influence = 1 - (dist - elementRadius) / (ANIMATED_HOVER_INFLUENCE_RADIUS - elementRadius);
      const maxT = sizePx * ANIMATED_MAX_TRANSLATE_RATIO;
      // Clamp magnitude to 1 so cursor exactly at center doesn't divide by zero
      const magnitude = Math.max(dist, 1);
      const tx = (dx / magnitude) * maxT * influence;
      const ty = (dy / magnitude) * maxT * influence;

      translateX.set(tx);
      translateY.set(ty);
    },
    [sizePx, translateX, translateY, rotate],
  );

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove, { passive: true });

    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [handleMouseMove]);

  return (
    <motion.div
      ref={containerRef}
      className={twJoin(
        "shrink-0 overflow-hidden rounded-full",
        size === "sm" ? "size-icon-control" : "size-control-md",
      )}
      whileHover={{ scale: 0.93 }}
      whileTap={{ scale: 0.82 }}
      transition={{ type: "spring", stiffness: 400, damping: 12 }}
      tabIndex={-1}
    >
      <RuntimeMotionTransformImage
        src={nobiLogo}
        alt=""
        x={springX}
        y={springY}
        scale={springScale}
        rotate={springRotate}
      />
    </motion.div>
  );
}
